import React, { useLayoutEffect, useRef } from "react";
import CustomTable from "components/ui-component/CustomTable";
import { DocumentAttachment } from "types/Documents";
import { TABLE_CONFIG } from "constants/documents";
import { createTableColumns } from "./TableColumns";
import { useTestAttrsFactory, useTid } from "utils/testid";

interface DesktopDocumentsTableProps {
  data: DocumentAttachment[];
  isTableLoading: boolean;
  isDownloading: string | null;
  timezone?: string;
  totalCount: number;
  perPage: number;
  currentPage: number;
  onPreview: (data: DocumentAttachment) => void;
  onDownload: (path: string, fileName: string, id: string) => void;
  onNextClick: () => void;
  onPreviousClick: () => void;
  onRowClick: (row: DocumentAttachment) => void;
}

const DesktopDocumentsTable: React.FC<DesktopDocumentsTableProps> = ({
  data,
  isTableLoading,
  isDownloading,
  timezone = "UTC",
  totalCount,
  perPage,
  currentPage,
  onPreview,
  onDownload,
  onNextClick,
  onPreviousClick,
  onRowClick,
}) => {
  const tid = useTid();
  const testAttrs = useTestAttrsFactory();
  const tableSectionRef = useRef<HTMLDivElement>(null);
  const previousPageRef = useRef(currentPage);
  const getDocumentRowKey = (row: DocumentAttachment) =>
    row.id || row.title || row.newTitle || "unknown";
  const columns = createTableColumns({
    isTableLoading,
    isDownloading,
    timezone,
    onPreview,
    onDownload,
    tid,
  });

  useLayoutEffect(() => {
    if (previousPageRef.current === currentPage) return;

    previousPageRef.current = currentPage;
    const tableScrollContainer =
      tableSectionRef.current?.querySelector(".dc-dial-table");
    tableScrollContainer?.scrollTo({ top: 0 });
  }, [currentPage]);

  return (
    <div
      ref={tableSectionRef}
      className="px-10 pt-10 lg:block hidden"
      {...testAttrs("desktop-table-section", "Documents table")}
    >
      <CustomTable
        columns={columns}
        data={data || []}
        fixedHeaderScrollHeight={TABLE_CONFIG.FIXED_HEADER_SCROLL_HEIGHT}
        perPage={perPage}
        currentPage={currentPage}
        totalCount={totalCount}
        isTableLoading={isTableLoading}
        onNextClick={onNextClick}
        onPreviousClick={onPreviousClick}
        onRowClick={(row: DocumentAttachment) => onRowClick(row)}
        data-testid={tid("desktop-table")}
        getRowTestId={(row: DocumentAttachment) =>
          tid(`desktop-row-${getDocumentRowKey(row)}`)
        }
      />
    </div>
  );
};

export default DesktopDocumentsTable;
